import { browser } from '$app/environment';
import { derived, type Readable, readonly, writable } from 'svelte/store';

type BreakpointSize = 'sm' | 'md' | 'lg';

const breakpoints: Record<BreakpointSize, number> = {
	sm: 640,
	md: 960,
	lg: 1920,
};

function createBreakpointObserver(): Readable<BreakpointSize> {
	if (!browser) {
		// TODO we have to prevent using this on the server!
		return readonly(writable<BreakpointSize>('md'));
	}

	const match: Record<BreakpointSize, MediaQueryList> = {
		sm: window.matchMedia(`(max-width: ${breakpoints.md}px)`),
		md: window.matchMedia(`(min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.lg}px)`),
		lg: window.matchMedia(`(min-width: ${breakpoints.lg}px)`),
	};
	const matchers: [BreakpointSize, MediaQueryList][] = Object.entries(match) as [
		BreakpointSize,
		MediaQueryList,
	][];
	const sizeByMedia: Record<string, BreakpointSize> = Object.fromEntries(
		matchers.map(([size, queryList]) => [queryList.media, size]),
	);

	const initialSize: BreakpointSize
		= matchers.find(([_, mediaQueryList]) => mediaQueryList.matches)?.[0] ?? 'sm';

	function setListeners() {
		matchers.forEach(([_, queryList]) => queryList.addEventListener('change', handleChange));
	}

	function removeListeners() {
		matchers.forEach(([_, queryList]) => queryList.removeEventListener('change', handleChange));
	}

	const store = writable<BreakpointSize>(initialSize, () => {
		setListeners();
		return removeListeners;
	});

	function handleChange(event: MediaQueryListEvent) {
		if (event.matches) {
			store.set(sizeByMedia[event.media]);
		}
	}

	return readonly(store);
}

export const isMobile = derived(createBreakpointObserver(), $breakpoint => $breakpoint === 'sm');

export const deviceType = derived(createBreakpointObserver(), ($breakpoint) => {
	switch ($breakpoint) {
		case 'sm':
			return 'mobile';
		case 'md':
			return 'tablet';
		case 'lg':
			return 'desktop';
	}
});
